import React, { useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import {
  parseCumulativeProductCatalogCountRatingResponse,
  parseWeeklyProductCatalogCountRatingData,
  useLifetimeProductCatalogCountRatingData,
  useWeeklyProductCatalogCountRatingData
} from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';

const productCatalogCountOptions = [
  { label: 'Product Catalog Count by Cumulative', id: 'cumulativeProductCatalogCount' },
  { label: 'Product Catalog Count by Weekly', id: 'weeklyProductCatalogCount' }
];

/**
 *
 * Ratings page in Review tab. Product Catalog Count by (Cumulative/Weekly) Rating Tier
 */

const ProductCatalogCountRatingChart = () => {
  const [selectedMetric, setSelectedMetric] = useState('cumulativeProductCatalogCount');
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();

  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();

  const { data: cumulativeData, isLoading: cumulativeLoading } = useLifetimeProductCatalogCountRatingData();
  const { data: weeklyData, isLoading: weeklyLoading } = useWeeklyProductCatalogCountRatingData({
    shouldStartFetch: selectedMetric === 'weeklyProductCatalogCount'
  });

  const { compareData, currentData } = cumulativeData
    ? parseCumulativeProductCatalogCountRatingResponse({
        data: cumulativeData,
        comparisonStartWeekToUse: comparisonStartWeek,
        comparisonEndWeek,
        selectedMetric
      })
    : { compareData: [], currentData: [] };

  const { compareData: weeklyCompareData, currentData: weeklyCurrentData } = weeklyData
    ? parseWeeklyProductCatalogCountRatingData({ data: weeklyData, field: 'rating' })
    : { compareData: [], currentData: [] };

  return (
    <BeaconChartWithLegend
      includeSummaryInfo={false}
      convertSeriesToDelimitedData={async () => {
        return saveBarChartAsDelimitedData(
          selectedMetric === 'cumulativeProductCatalogCount' ? currentData : weeklyCurrentData,
          selectedMetric === 'cumulativeProductCatalogCount' ? compareData : weeklyCompareData,
          { metricLabel: 'Product Catalog Count' }
        );
      }}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the number of products for ${entityPhrase} `}
            {taxonomyText} in each star rating tier
            {selectedMetric === 'cumulativeProductCatalogCount'
              ? '.'
              : ` during the
            ${mainTimePeriodShortDisplayName} period compared to the ${comparisonTimePeriodShortDisplayName}.`}
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      singleDropdownProps={{
        defaultLabel: '',
        options: productCatalogCountOptions,
        selectedId: selectedMetric,
        onChange: (option) => setSelectedMetric(option.id),
        suffix: 'Rating Tier'
      }}
      primaryLegendProps={{
        displayName: legendLabel
      }}
      comparisonLegendProps={{
        displayName: legendComparisonLabel
      }}
      loading={cumulativeLoading || weeklyLoading}
      loadingComponent={<SplineChartLoading width={1000} />}
    >
      <TopEntityChartV2
        xAxisLabelTextStyles={{ lineHeight: 1.6 }}
        xAxisFormatter={(xAxisCategory) => {
          return ReactDOMServer.renderToStaticMarkup(
            <GenericXAxisLabel xAxisCategory={xAxisCategory} wrapperStyle={{ width: '50px' }} />
          );
        }}
        chartSeries={[
          {
            type: 'column',
            color: '#adbdcc',
            data: selectedMetric === 'cumulativeProductCatalogCount' ? compareData : weeklyCompareData,
            metricType: METRICTYPE.VOLUME
          },
          {
            type: 'column',
            data: selectedMetric === 'cumulativeProductCatalogCount' ? currentData : weeklyCurrentData,
            metricType: METRICTYPE.VOLUME
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export default ProductCatalogCountRatingChart;
